import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Phone } from 'lucide-react';

const ServiceDetail = () => {
  const { serviceId } = useParams();

  const servicesData = {
    gst: {
      title: 'GST Services',
      subtitle: 'Comprehensive GST Compliance & Advisory Solutions',
      description: 'We assist businesses in managing end-to-end GST compliance including registration, filing, reconciliation, and representation before authorities.',
      image: 'https://images.pexels.com/photos/8441820/pexels-photo-8441820.jpeg?auto=compress&cs=tinysrgb&w=800',
      whoIsItFor: [
        'Small and Medium Enterprises',
        'Startups and New Businesses',
        'E-commerce Businesses',
        'Service Providers',
        'Manufacturers and Traders'
      ],
      benefits: [
        'Timely GST return filing',
        'Maximum ITC utilization',
        'Reduced compliance risk',
        'Expert notice handling',
        'Penalty avoidance'
      ],
      process: [
        {
          title: 'Data Collection',
          description: 'Gather all invoices, bills, and transaction records'
        },
        {
          title: 'Reconciliation',
          description: 'Match input tax credit and verify data accuracy'
        },
        {
          title: 'Filing & Compliance',
          description: 'File GSTR-1, GSTR-3B and other required returns'
        },
        {
          title: 'Ongoing Support',
          description: 'Continuous advisory and GST updates'
        }
      ]
    },
    'income-tax': {
      title: 'Income Tax Services',
      subtitle: 'Expert Income Tax Filing & Planning',
      description: 'Professional income tax services covering ITR filing, tax planning, assessment handling, and representation for individuals and businesses.',
      image: 'https://images.unsplash.com/photo-1710492341412-8b3aee7e70a6?auto=format&fit=crop&w=800&q=80',
      whoIsItFor: [
        'Salaried Individuals',
        'Business Owners & Professionals',
        'Freelancers & Consultants',
        'Companies & Firms',
        'NRIs'
      ],
      benefits: [
        'Maximum tax savings',
        'Error-free ITR filing',
        'Quick refund processing',
        'Expert tax planning',
        'Assessment support'
      ],
      process: [
        {
          title: 'Document Collection',
          description: 'Gather Form 16, bank statements, investment proofs'
        },
        {
          title: 'Tax Computation',
          description: 'Calculate taxable income and tax liability'
        },
        {
          title: 'ITR Filing',
          description: 'File appropriate ITR form with optimal deductions'
        },
        {
          title: 'Follow-up',
          description: 'Track refund status and handle queries'
        }
      ]
    },
    audit: {
      title: 'Audit & Assurance',
      subtitle: 'Professional Audit Services',
      description: 'Comprehensive audit services including statutory audit, tax audit, and internal audit ensuring compliance and financial accuracy.',
      image: 'https://images.pexels.com/photos/3727513/pexels-photo-3727513.jpeg?auto=compress&cs=tinysrgb&w=800',
      whoIsItFor: [
        'Private Limited Companies',
        'Partnership Firms',
        'Trusts & Societies',
        'Businesses above turnover threshold',
        'Organizations requiring internal audit'
      ],
      benefits: [
        'Regulatory compliance',
        'Financial accuracy assurance',
        'Risk identification',
        'Improved internal controls',
        'Stakeholder confidence'
      ],
      process: [
        {
          title: 'Planning',
          description: 'Understand business operations and audit scope'
        },
        {
          title: 'Execution',
          description: 'Verify financial records and internal controls'
        },
        {
          title: 'Reporting',
          description: 'Prepare audit report with findings'
        },
        {
          title: 'Closure',
          description: 'Finalize accounts and compliance filing'
        }
      ]
    },
    accounting: {
      title: 'Accounting & Compliance',
      subtitle: 'Complete Accounting Solutions',
      description: 'End-to-end accounting services including bookkeeping, TDS compliance, payroll management, and ROC filings.',
      image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=800&q=80',
      whoIsItFor: [
        'Small Businesses',
        'Growing Enterprises',
        'Companies requiring regular compliance',
        'Organizations with employees',
        'Businesses seeking outsourced accounting'
      ],
      benefits: [
        'Accurate financial records',
        'Timely TDS compliance',
        'Efficient payroll processing',
        'ROC compliance maintenance',
        'Cost-effective solutions'
      ],
      process: [
        {
          title: 'Setup',
          description: 'Establish accounting system and processes'
        },
        {
          title: 'Recording',
          description: 'Maintain day-to-day books of accounts'
        },
        {
          title: 'Compliance',
          description: 'File TDS returns and ROC forms'
        },
        {
          title: 'Reporting',
          description: 'Provide financial statements and MIS reports'
        }
      ]
    },
    advisory: {
      title: 'Business Advisory',
      subtitle: 'Strategic Business Consulting',
      description: 'Strategic advisory services including business registration, financial planning, loan assistance, and business structuring.',
      image: 'https://images.pexels.com/photos/8441820/pexels-photo-8441820.jpeg?auto=compress&cs=tinysrgb&w=800',
      whoIsItFor: [
        'Startups & New Ventures',
        'Existing Businesses',
        'Entrepreneurs',
        'Businesses seeking funding',
        'Companies planning expansion'
      ],
      benefits: [
        'Proper business structure',
        'Strategic financial planning',
        'Loan facilitation support',
        'Tax-efficient structuring',
        'Expert guidance'
      ],
      process: [
        {
          title: 'Consultation',
          description: 'Understand business goals and requirements'
        },
        {
          title: 'Strategy',
          description: 'Develop tailored business and financial plan'
        },
        {
          title: 'Implementation',
          description: 'Execute registrations and structuring'
        },
        {
          title: 'Support',
          description: 'Ongoing advisory and compliance assistance'
        }
      ]
    },
    financial: {
      title: 'Financial Services',
      subtitle: 'Comprehensive Loan & Financing Solutions',
      description: 'Expert assistance in securing various types of loans and financing options for individuals and businesses. We help you navigate the loan process smoothly.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80',
      whoIsItFor: [
        'Business Owners seeking expansion',
        'Individuals needing personal loans',
        'Property owners seeking LAP',
        'Home buyers requiring housing loans',
        'Businesses needing working capital'
      ],
      benefits: [
        'Competitive interest rates',
        'Quick loan processing',
        'Multiple lender options',
        'Expert documentation support',
        'End-to-end assistance'
      ],
      process: [
        {
          title: 'Requirement Analysis',
          description: 'Understand your financial needs and eligibility'
        },
        {
          title: 'Documentation',
          description: 'Prepare and verify all required documents'
        },
        {
          title: 'Lender Matching',
          description: 'Connect with best suitable lenders'
        },
        {
          title: 'Disbursement',
          description: 'Follow-up till loan approval and disbursement'
        }
      ]
    }
  };

  const service = servicesData[serviceId];

  if (!service) {
    return (
      <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
        <h2>Service not found</h2>
        <Link to="/services" className="btn btn-primary" style={{ marginTop: '20px' }}>
          Back to Services
        </Link>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      {/* Hero Section */}
      <section className="service-detail-hero">
        <div className="service-detail-hero-overlay"></div>
        <img src={service.image} alt={service.title} className="service-detail-hero-img" />
        <div className="container">
          <Link to="/services" className="back-link">
            <ArrowLeft size={20} />
            Back to Services
          </Link>
          <h1 className="service-detail-hero-title">{service.title}</h1>
          <p className="service-detail-hero-subtitle">{service.subtitle}</p>
        </div>
      </section>

      {/* Overview */}
      <section className="service-detail-overview">
        <div className="container">
          <p className="service-detail-description">{service.description}</p>
        </div>
      </section>

      {/* Who Is It For */}
      <section className="service-detail-section">
        <div className="container">
          <div className="service-detail-content-grid">
            <div>
              <h2 className="service-detail-section-title">Who Is It For?</h2>
              <ul className="service-detail-list">
                {service.whoIsItFor.map((item, index) => (
                  <li key={index} className="service-detail-list-item">
                    <CheckCircle className="list-check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="service-detail-section-title">Benefits</h2>
              <ul className="service-detail-list">
                {service.benefits.map((item, index) => (
                  <li key={index} className="service-detail-list-item">
                    <CheckCircle className="list-check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="service-detail-process">
        <div className="container">
          <h2 className="section-title-center">Our Process</h2>
          <div className="process-steps-grid">
            {service.process.map((step, index) => (
              <div key={index} className="process-step-card">
                <div className="process-step-number">{index + 1}</div>
                <h3 className="process-step-title">{step.title}</h3>
                <p className="process-step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-detail-cta">
        <div className="container">
          <div className="service-detail-cta-content">
            <h2 className="service-detail-cta-title">Ready to Get Started?</h2>
            <p className="service-detail-cta-text">
              Schedule a consultation to discuss your requirements
            </p>
            <div className="service-detail-cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Schedule Consultation
              </Link>
              <a href="tel:+916396550622" className="btn btn-secondary">
                <Phone size={20} />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
